import { useCallback, useEffect, useRef, useState } from "react";
import { resolvePath, useFetcher, useNavigation, useSearchParams } from "react-router-dom";
import { objectToSearchParams, removeEmpty, searchParamsToObject } from '../library/helpers';

function useFetch({ initialData, autoLoad = false }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const fetcher = useFetcher()
  const navigation = useNavigation()
  const [hasMore, setHasMore] = useState(true)
  const searchParamsParsed = searchParamsToObject(searchParams)
  const searchParamsKey = searchParams.toString()
  const [list, setList] = useState(initialData?.data || [])
  const currentPage = useRef(1)
  const searchParamsRef = useRef(searchParamsParsed)
  const loadMoreSearchParamsKeyRef = useRef(null)
  const initialLocation = useRef(resolvePath('.'));

  useEffect(() => {
    initialLocation.current = resolvePath('.', location.pathname)?.pathname;
    return () => {
      initialLocation.current = null
      currentPage.current = 1;
    };
  }, []);

  useEffect(() => {
    setHasMore(false)
    searchParamsRef.current = searchParamsParsed
    loadMoreSearchParamsKeyRef.current = null
    currentPage.current = 1
  }, [searchParamsKey])

  useEffect(() => {
    setList(initialData.data)
    setHasMore(initialData.meta.totalPages > parseInt(initialData.meta.page))
    searchParamsRef.current = searchParamsParsed
    currentPage.current = 1
  }, [initialData])

  useEffect(() => {
    if (!fetcher.data || fetcher.state === "loading") return;

    if (loadMoreSearchParamsKeyRef.current !== searchParamsKey) {
      return
    }

    setList(prev => [...prev, ...(fetcher.data.data ?? [])])
    currentPage.current = fetcher?.data?.meta?.page ?? 1
    setHasMore(fetcher.data?.meta
      ? fetcher.data.meta.totalPages > parseInt(fetcher.data.meta.page)
      : false)
  }, [fetcher.data, fetcher.state, searchParamsKey])

  // Self-chaining: fires on mount (initialData) and after every page completes (fetcher.state).
  // Drives sequential loading of all pages without requiring scroll.
  // Uses refs + initialData directly to avoid stale hasMore closure on filter changes.
  useEffect(() => {
    if (!autoLoad) return
    if (fetcher.state !== 'idle' || !initialLocation.current) return
    if (navigation.state !== 'idle') return
    if (currentPage.current >= (initialData?.meta?.totalPages ?? 1)) return

    currentPage.current++
    const params = new URLSearchParams(objectToSearchParams(searchParamsRef.current))
    params.set('page', currentPage.current)
    params.set('index', '')
    loadMoreSearchParamsKeyRef.current = new URLSearchParams(objectToSearchParams(searchParamsRef.current)).toString()
    fetcher.load(`${initialLocation.current}?${params.toString()}`)
  }, [initialData, fetcher.state, navigation.state, autoLoad])

  const setFilters = useCallback((f) => {
    setSearchParams((params) => {
      const currentParams = searchParamsToObject(params)
      const nextFilters = typeof f === 'function' ? f(currentParams.filter) : f

      return objectToSearchParams({
        ...currentParams,
        filter: removeEmpty(nextFilters ?? {}),
      })
    })
  }, [setSearchParams])

  const setSort = useCallback((s) => {
    setSearchParams((params) => objectToSearchParams({
      ...searchParamsToObject(params),
      sort: removeEmpty(s ?? {}),
    }))
  }, [setSearchParams])

  const loadMore = useCallback(() => {
    if (fetcher.state === 'loading' || !hasMore || !initialLocation.current) return;

    currentPage.current++
    const params = new URLSearchParams(objectToSearchParams(searchParamsRef.current))
    params.set('page', currentPage.current)
    params.set('index', '')
    loadMoreSearchParamsKeyRef.current = new URLSearchParams(objectToSearchParams(searchParamsRef.current)).toString()
    fetcher.load(`${initialLocation.current}?` + params.toString())
  }, [fetcher, hasMore]);

  // isLoading reflects route navigation (initial load + filter/sort changes),
  // NOT fetcher.load() calls — those are "load more" and use the bottom spinner
  const isLoading = navigation.state === 'loading'

  return [list, hasMore, isLoading, searchParamsParsed, {
    setSort,
    setFilters,
    loadMore
  }];
}

export default useFetch;
